import { useState, useCallback, createContext, useContext, useRef } from 'react';
import { ToastContainer, Toast, Row, Col, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faXmark, faCheck, faTriangleExclamation, faInfo } from '@fortawesome/pro-light-svg-icons';

// ─── Icon config — identical to original ─────────────────────────────────────

const ICON_CFG = {
  success: { icon: faCheck,              color: '#5cb85c', title: 'Success' },
  error:   { icon: faXmark,              color: '#d9534f', title: 'Error'   },
  warning: { icon: faTriangleExclamation, color: '#FFB81C', title: 'Warning' },
  info:    { icon: faInfo,               color: '#00778B', title: 'Info'    },
  default: { icon: faCheck,              color: '#00778B', title: 'Info'    },
};

// ─── Single toast item ────────────────────────────────────────────────────────

const AlertItem = ({ id, type, msg, autoClose, onClose }) => {
  const cfg = ICON_CFG[type] ?? ICON_CFG.default;
  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShow(false);
    // Wait for fade out transition to complete before removing from DOM
    setTimeout(() => onClose(id), 300);
  };

  return (
    <Toast
      className="toast py-1"
      show={show}
      onClose={handleClose}
      autohide={!!autoClose}
      bg={'light'}
      delay={typeof autoClose === 'number' ? autoClose : 3000}
      animation={true}
    >
      <Toast.Body>
        <Container>
          <Row className="align-items-start justify-content-between">
            <Col sm="auto">
              <div className="fa-layers fa-xl">
                <FontAwesomeIcon icon={faCircle} transform="grow-3" color={cfg.color} />
                <FontAwesomeIcon transform="shrink-3" icon={cfg.icon} color="white" />
              </div>
            </Col>
            <Col sm="8" className="px-0">
              <div className="d-flex align-items-center flex-grow-1">{msg}</div>
            </Col>
            <Col sm="auto" className="">
              <FontAwesomeIcon
                className="fa-lg toast-close-icon"
                icon={faXmark}
                onClick={handleClose}
              />
            </Col>
          </Row>
        </Container>
      </Toast.Body>
    </Toast>
  );
};

// ─── Context ──────────────────────────────────────────────────────────────────

const AlertsContext = createContext(null);

export const AlertsProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const counterRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((type, props) => {
    const { msg, title, id: toastId, autoClose } = props;

    // Deduplicate by toastId if provided — mirrors react-toastify toastId behaviour
    if (toastId) {
      setToasts(prev => {
        if (prev.some(t => t.toastId === toastId)) return prev;
        return [...prev, { id: ++counterRef.current, toastId, type, msg, title, autoClose }];
      });
    } else {
      setToasts(prev => [...prev, { id: ++counterRef.current, type, msg, title, autoClose }]);
    }
  }, []);

  // ── alerts object — same API as the original Alerts class ──────────────────
  const alerts = {
    success: (props) => show('success', { autoClose: 3000,  ...props }),
    error:   (props) => show('error',   { autoClose: false, ...props }),
    warning: (props) => show('warning', { autoClose: false, ...props }),
    info:    (props) => show('info',    { autoClose: false, ...props }),
    default: (props) => show('default', { autoClose: true,  ...props }),
  };

  return (
    <AlertsContext.Provider value={alerts}>
      {children}

      {/* Bottom-left toast stack — mirrors react-toastify position="bottom-left" */}
      <ToastContainer
        position="bottom-start"
        className="p-3"
      >
        {toasts.map(t => (
          <AlertItem
            key={t.id}
            id={t.id}
            type={t.type}
            msg={t.msg}
            autoClose={t.autoClose}
            onClose={dismiss}
          />
        ))}
      </ToastContainer>
    </AlertsContext.Provider>
  );
};

// ─── withAlerts HOC — identical external API ─────────────────────────────────

const withAlerts = (Component) => {
  return (props) => {
    const alerts = useContext(AlertsContext);
    return <Component {...props} alerts={alerts} />;
  };
};

export default withAlerts;
